import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { UI_PREFS_KEYS, getUiPref, setUiPref } from './utils/browserStorage';
import {
  DEFAULT_LOCALE,
  LOCALES,
  applyHtmlLang,
  getLocale,
  setLocale as setI18nLocale,
  subscribeLocale,
  t as translate,
} from './i18n';

const normalizeLocale = (value) => (value === LOCALES.ru ? LOCALES.ru : LOCALES.en);

const readStoredLocale = () => {
  try {
    return normalizeLocale(getUiPref(UI_PREFS_KEYS.locale));
  } catch {
    return DEFAULT_LOCALE;
  }
};

// Apply before first paint to keep <html lang> in sync.
const bootLocale = readStoredLocale();
setI18nLocale(bootLocale);
applyHtmlLang(bootLocale);

const LocaleContext = createContext({
  locale: DEFAULT_LOCALE,
  setLocale: () => {},
  t: translate,
});

export const LocaleProvider = ({ children }) => {
  const [locale, setLocaleState] = useState(getLocale);

  useEffect(() => {
    applyHtmlLang(locale);
    setUiPref(UI_PREFS_KEYS.locale, locale);
  }, [locale]);

  useEffect(() => subscribeLocale((next) => {
    setLocaleState(next);
  }), []);

  const setLocale = useCallback((next) => {
    setLocaleState(setI18nLocale(next));
  }, []);

  const t = useCallback((key, vars) => translate(key, vars), [locale]);

  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
};

export const useLocale = () => useContext(LocaleContext);

export const useT = () => useLocale().t;

export { LOCALES };
